/**
 * GridCrash — Game Engine
 * Pure, framework-free TypeScript state machine.
 * Strict mode enforced — no `any`, no implicit types.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type GamePhase = "IDLE" | "PLAY" | "WIN" | "LOSE";

export interface Cell {
  isMine: boolean;
  isRevealed: boolean;
  isFlagged: boolean;
  adjacentMines: number;
}

export interface DifficultyConfig {
  id: "easy" | "medium" | "hard";
  label: string;
  rows: number;
  cols: number;
  mineCount: number;
  leaderboardKey: string;
  /** Win bonus awarded on full board clear. Scales with difficulty. */
  winBonus: number;
  /** Speed bonus tiers: array of [maxSeconds, bonusPoints] pairs, ascending by time. */
  speedTiers: Array<[number, number]>;
  /** Optional countdown (6B): the game is lost when elapsed reaches this. */
  timeLimitSecs?: number;
}

export interface GameState {
  grid: Cell[][];
  phase: GamePhase;
  score: number;
  timeElapsed: number;
  difficulty: DifficultyConfig;
  safeCellsRemaining: number;
  flagsPlaced: number;
}

export type RevealResult = "NO_CHANGE" | "REVEALED_SAFE" | "LOST";

export type GenerationMode = "random" | "deterministic";

/** Aftershock mode (6C): mines migrate every N successful reveals. */
export interface AftershockConfig {
  revealsPerTremor: number;
}

// ─── Difficulty Configs ───────────────────────────────────────────────────────

export const DIFFICULTIES: Record<string, DifficultyConfig> = {
  easy: {
    id: "easy",
    label: "Easy",
    rows: 9,
    cols: 9,
    mineCount: 10,
    leaderboardKey: "gridcrash.lb.easy",
    winBonus: 100,
    // Speed bonus tiers: [maxSeconds, bonusPoints]
    // Complete under 30s = +100, under 60s = +75, etc.
    speedTiers: [[30, 100], [60, 75], [120, 50], [300, 25]],
  },
  medium: {
    id: "medium",
    label: "Medium",
    rows: 16,
    cols: 16,
    mineCount: 40,
    leaderboardKey: "gridcrash.lb.medium",
    winBonus: 250,  // 2.5× Easy — 40 mines warrants a bigger reward
    speedTiers: [[90, 100], [180, 75], [360, 50], [600, 25]],
  },
  hard: {
    id: "hard",
    label: "Expert",
    rows: 16,
    cols: 30,
    mineCount: 99,
    leaderboardKey: "gridcrash.lb.hard",
    winBonus: 500,  // 5× Easy — 99 mines is a real achievement
    // Speed tiers doubled vs original — Expert takes much longer than Easy
    speedTiers: [[360, 100], [720, 75], [1200, 50], [2400, 25]],
  },
};

// ─── Engine Class ─────────────────────────────────────────────────────────────

export class GridCrashEngine {
  private state: GameState;
  private timerHandle: ReturnType<typeof setInterval> | null = null;
  private timerStartTime: number | null = null; // wall-clock ms when timer started (2.0-B2)
  private generationMode: GenerationMode;
  private rng: (() => number) | null = null; // seeded RNG for daily boards (2.0-B4)

  // Aftershock mode (6C)
  private aftershock: AftershockConfig | null = null;
  private baseSeed: number | null = null;
  private revealsSinceTremor = 0;
  private tremorIndex = 0;

  // Callbacks
  public onStateChange: ((state: Readonly<GameState>) => void) | null = null;
  public onGameEnded: ((score: number, key: string) => void) | null = null;
  /** Fired after mines migrate in aftershock mode (before the state emit). */
  public onTremor: ((minesMoved: number) => void) | null = null;

  constructor(
    difficulty: DifficultyConfig,
    mode: GenerationMode = "random",
    seed?: number,  // optional — daily boards pass today's seed for reproducibility
    aftershock?: AftershockConfig
  ) {
    this.generationMode = mode;
    if (seed !== undefined) {
      this.baseSeed = seed;
      this.rng = this.makeSeededRng(seed);
    }
    this.aftershock = aftershock ?? null;
    this.state = this.buildInitialState(difficulty);
  }

  /** xorshift32 seeded RNG — same algorithm as daily.ts seededRng() */
  private makeSeededRng(seed: number): () => number {
    let s = seed || 1;
    return function(): number {
      s ^= s << 13;
      s ^= s >> 17;
      s ^= s << 5;
      s = s >>> 0;
      return s / 0xFFFFFFFF;
    };
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  getState(): Readonly<GameState> {
    return this.state;
  }

  /**
   * Attempt to reveal a cell. If this is the first move, mines are placed
   * after the click so the first cell and its 8 neighbors are always safe.
   */
  revealCell(row: number, col: number): RevealResult {
    const { phase, grid } = this.state;
    const cell = grid[row][col];

    if (phase === "IDLE") {
      this.plantMines(row, col);
      this.startTimer();
    }

    if (this.state.phase !== "PLAY") return "NO_CHANGE";
    if (cell.isRevealed || cell.isFlagged) return "NO_CHANGE";

    // Mark revealed
    this.mutateCell(row, col, { isRevealed: true });

    if (this.state.grid[row][col].isMine) {
      this.triggerLoss(row, col);
      return "LOST";
    }

    this.state.safeCellsRemaining -= 1;

    // Cascade reveal on blank cells
    if (this.state.grid[row][col].adjacentMines === 0) {
      this.cascadeReveal(row, col);
    }

    if (this.state.safeCellsRemaining === 0) {
      this.triggerWin();
    }

    // Aftershock (6C): every N successful reveals, the mines migrate.
    if (this.aftershock && this.state.phase === "PLAY") {
      this.revealsSinceTremor++;
      if (this.revealsSinceTremor >= this.aftershock.revealsPerTremor) {
        this.revealsSinceTremor = 0;
        const moved = this.performTremor();
        if (moved > 0) this.onTremor?.(moved);
      }
    }

    this.emit();
    return "REVEALED_SAFE";
  }

  /** Reveals until the next tremor; null when not in aftershock mode or game over. */
  get tremorCountdown(): number | null {
    if (!this.aftershock) return null;
    if (this.state.phase === "WIN" || this.state.phase === "LOSE") return null;
    return this.aftershock.revealsPerTremor - this.revealsSinceTremor;
  }

  /** Toggle flag on an unrevealed cell. Returns whether state changed. */
  toggleFlag(row: number, col: number): boolean {
    const { phase, grid } = this.state;
    if (phase !== "PLAY") return false;
    const cell = grid[row][col];
    if (cell.isRevealed) return false;

    const wasSet = cell.isFlagged;
    this.mutateCell(row, col, { isFlagged: !wasSet });
    this.state.flagsPlaced += wasSet ? -1 : 1;

    this.emit();
    return true;
  }

  /**
   * Pick a guaranteed-safe cell for the hint lifeline. Prefers frontier
   * cells (unrevealed safe cells touching a revealed one) so the hint
   * extends the player's known territory instead of opening a random
   * far corner. Returns null when the game isn't in progress or no
   * safe cell remains. Selection is not seeded — the resulting reveal
   * is recorded in the replay stream, which is all playback needs.
   */
  findHintCell(): { row: number; col: number } | null {
    if (this.state.phase !== "PLAY") return null;
    const { rows, cols } = this.state.difficulty;

    const frontier: Array<{ row: number; col: number }> = [];
    const interior: Array<{ row: number; col: number }> = [];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = this.state.grid[r][c];
        if (cell.isRevealed || cell.isFlagged || cell.isMine) continue;

        let touchesRevealed = false;
        outer: for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = r + dr;
            const nc = c + dc;
            if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
            if (this.state.grid[nr][nc].isRevealed) {
              touchesRevealed = true;
              break outer;
            }
          }
        }
        (touchesRevealed ? frontier : interior).push({ row: r, col: c });
      }
    }

    const pool = frontier.length > 0 ? frontier : interior;
    if (pool.length === 0) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  /**
   * Lifeline support (6D): convert an unrevealed mine into a safe cell —
   * the Gauntlet's Seismograph relic absorbs a fatal tap this way.
   * The difficulty is cloned before its mineCount drops so shared
   * configs (DIFFICULTIES) are never mutated.
   */
  disarmMine(row: number, col: number): boolean {
    if (this.state.phase !== "PLAY") return false;
    const cell = this.state.grid[row][col];
    if (!cell.isMine || cell.isRevealed) return false;

    this.mutateCell(row, col, { isMine: false });
    this.state.difficulty = {
      ...this.state.difficulty,
      mineCount: this.state.difficulty.mineCount - 1,
    };
    this.state.safeCellsRemaining += 1;
    this.calculateAdjacency();
    this.emit();
    return true;
  }

  /** Reset to IDLE with same or new difficulty. */
  reset(difficulty?: DifficultyConfig): void {
    this.stopTimer();
    const d = difficulty ?? this.state.difficulty;
    this.state = this.buildInitialState(d);
    this.emit();
  }

  /** Expose minesRemaining for HUD display. */
  get minesRemaining(): number {
    return this.state.difficulty.mineCount - this.state.flagsPlaced;
  }

  /** Expose current real-time score (updated each second). */
  get liveScore(): number {
    return this.computeScore(false);
  }

  // ─── Internals: State Setup ──────────────────────────────────────────────────

  private buildInitialState(difficulty: DifficultyConfig): GameState {
    const { rows, cols, mineCount } = difficulty;
    const grid: Cell[][] = Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => ({
        isMine: false,
        isRevealed: false,
        isFlagged: false,
        adjacentMines: 0,
      }))
    );

    return {
      grid,
      phase: "IDLE",
      score: 0,
      timeElapsed: 0,
      difficulty,
      safeCellsRemaining: rows * cols - mineCount,
      flagsPlaced: 0,
    };
  }

  // ─── Internals: Mine Placement ────────────────────────────────────────────────

  /**
   * Plant mines after first tap.
   * The clicked cell and its 8 neighbors are guaranteed mine-free.
   * Supports both random and deterministic (solvable) generation modes.
   */
  private plantMines(safeRow: number, safeCol: number): void {
    const { rows, cols, mineCount } = this.state.difficulty;

    // Build set of protected cell indices
    const protectedSet = new Set<number>();
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        const nr = safeRow + dr;
        const nc = safeCol + dc;
        if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
          protectedSet.add(nr * cols + nc);
        }
      }
    }

    if (this.generationMode === "deterministic") {
      this.plantMinesDeterministic(protectedSet, mineCount);
    } else {
      this.plantMinesRandom(protectedSet, mineCount);
    }

    this.calculateAdjacency();
    this.state.phase = "PLAY";
  }

  /**
   * Random mine placement (classic Minesweeper behavior).
   * Fisher-Yates shuffle on eligible cells for uniform distribution.
   */
  private plantMinesRandom(
    protectedSet: Set<number>,
    mineCount: number
  ): void {
    const { rows, cols } = this.state.difficulty;
    const eligible: number[] = [];
    const rand = this.rng ?? Math.random; // use seeded RNG for daily boards

    for (let i = 0; i < rows * cols; i++) {
      if (!protectedSet.has(i)) eligible.push(i);
    }

    // Fisher-Yates partial shuffle — pick first `mineCount` slots
    for (let i = 0; i < mineCount; i++) {
      const j = i + Math.floor(rand() * (eligible.length - i));
      [eligible[i], eligible[j]] = [eligible[j], eligible[i]];
      const idx = eligible[i];
      const r = Math.floor(idx / cols);
      const c = idx % cols;
      this.state.grid[r][c].isMine = true;
    }
  }

  /**
   * Deterministic mine placement that produces boards solvable without guessing.
   *
   * Strategy:
   * 1. Place mines in structured bands away from the safe zone.
   * 2. After each placement, run a logical solvability scan.
   * 3. If a mine placement would create an unsolvable 50/50, defer it
   *    to a less constrained region.
   *
   * This implements the "constraint satisfaction" approach: mines are placed
   * in positions that, combined with adjacency numbers, produce logically
   * deducible boards using 1-1, 1-2-1, and 1-2-2-1 boundary patterns.
   */
  private plantMinesDeterministic(
    protectedSet: Set<number>,
    mineCount: number
  ): void {
    const { rows, cols } = this.state.difficulty;

    // Collect eligible cells sorted by distance from center (outermost first)
    // This ensures mines cluster near edges, creating identifiable border patterns
    const centerR = rows / 2;
    const centerC = cols / 2;

    const eligible: Array<{ idx: number; dist: number }> = [];
    for (let i = 0; i < rows * cols; i++) {
      if (protectedSet.has(i)) continue;
      const r = Math.floor(i / cols);
      const c = i % cols;
      const dist = Math.abs(r - centerR) + Math.abs(c - centerC);
      eligible.push({ idx: i, dist });
    }

    // Sort: place mines further from center first (border-anchored)
    eligible.sort((a, b) => b.dist - a.dist);

    // Shuffle within distance tiers to preserve unpredictability
    this.shuffleInTiers(eligible, 2);

    let placed = 0;
    for (const { idx } of eligible) {
      if (placed >= mineCount) break;
      const r = Math.floor(idx / cols);
      const c = idx % cols;

      // Avoid placing mines that isolate a single unrevealed safe cell
      // (a 1-cell island would be a forced guess)
      if (!this.wouldCreateIsolatedGuess(r, c, protectedSet)) {
        this.state.grid[r][c].isMine = true;
        placed++;
      }
    }

    // If we couldn't place all mines deterministically, fall back to random
    // for any remaining (rare edge case in very small grids)
    if (placed < mineCount) {
      const remaining = mineCount - placed;
      const fallbackEligible: number[] = eligible
        .filter(({ idx }) => {
          const r = Math.floor(idx / cols);
          const c = idx % cols;
          return !this.state.grid[r][c].isMine;
        })
        .map(({ idx }) => idx);
      this.plantMinesRandom(
        new Set([...protectedSet, ...Array.from({ length: rows * cols }, (_, i) => i).filter((i) => {
          const r = Math.floor(i / cols);
          const c = i % cols;
          return this.state.grid[r][c].isMine;
        })]),
        Math.min(remaining, fallbackEligible.length)
      );
    }
  }

  /** Shuffle items within ±tier distance buckets to randomize while keeping structure. */
  private shuffleInTiers(
    items: Array<{ idx: number; dist: number }>,
    tierSize: number
  ): void {
    let i = 0;
    while (i < items.length) {
      const tierDist = items[i].dist;
      let j = i;
      while (j < items.length && Math.abs(items[j].dist - tierDist) <= tierSize) {
        j++;
      }
      // Shuffle tier [i, j)
      for (let k = i; k < j - 1; k++) {
        const randIdx = k + Math.floor(Math.random() * (j - k));
        [items[k], items[randIdx]] = [items[randIdx], items[k]];
      }
      i = j;
    }
  }

  /**
   * Heuristic: a mine placement creates an "isolated guess" if it would
   * make a cell completely surrounded by mines on all sides, leaving no
   * adjacent numbered cell to logically deduce it from.
   */
  private wouldCreateIsolatedGuess(
    row: number,
    col: number,
    protectedSet: Set<number>
  ): boolean {
    const { rows, cols } = this.state.difficulty;

    // Count how many of this cell's neighbors are already mines
    let neighborMines = 0;
    let neighborSafe = 0;
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const nr = row + dr;
        const nc = col + dc;
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
        const nIdx = nr * cols + nc;
        if (this.state.grid[nr][nc].isMine) {
          neighborMines++;
        } else if (!protectedSet.has(nIdx)) {
          neighborSafe++;
        }
      }
    }

    // If all non-protected neighbors are mines, placing here creates an island
    return neighborSafe === 0 && neighborMines >= 3;
  }

  // ─── Internals: Aftershock Tremors (6C) ──────────────────────────────────────

  /**
   * Migrate each unrevealed, unflagged mine one step in a direction drawn
   * from a per-tremor seeded RNG chain, so a given seed always produces the
   * same quake sequence. Bedrock invariant: mines never enter revealed or
   * flagged cells (flagging territory locks it down — that's the strategy).
   * Mines with no legal destination stay put. Returns how many moved.
   */
  private performTremor(): number {
    const { rows, cols } = this.state.difficulty;
    this.tremorIndex++;

    const tremorSeed = (((this.baseSeed ?? 1) + this.tremorIndex * 7919) >>> 0) || 1;
    const rng = this.makeSeededRng(tremorSeed);

    const dirs: Array<[number, number]> = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1],           [0, 1],
      [1, -1],  [1, 0],  [1, 1],
    ];

    // Every tremor has a prevailing drift; each mine jitters around it.
    const drift = dirs[Math.floor(rng() * dirs.length)];

    // Snapshot movable mines first so a mine relocated down/right isn't
    // re-processed when the scan reaches its new cell.
    const movable: Array<[number, number]> = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = this.state.grid[r][c];
        if (cell.isMine && !cell.isRevealed && !cell.isFlagged) movable.push([r, c]);
      }
    }

    let moved = 0;
    for (const [r, c] of movable) {
      // Try drift first, then jittered alternatives, deterministically.
      const jitter = Math.floor(rng() * dirs.length);
      const candidates = [drift, dirs[jitter], dirs[(jitter + 3) % dirs.length]];

      for (const [dr, dc] of candidates) {
        const nr = r + dr;
        const nc = c + dc;
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
        const target = this.state.grid[nr][nc];
        if (target.isMine || target.isRevealed || target.isFlagged) continue;

        this.mutateCell(r, c, { isMine: false });
        this.mutateCell(nr, nc, { isMine: true });
        moved++;
        break;
      }
    }

    if (moved > 0) this.calculateAdjacency();
    return moved;
  }

  // ─── Internals: Adjacency & Cascade ──────────────────────────────────────────

  private calculateAdjacency(): void {
    const { rows, cols } = this.state.difficulty;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.state.grid[r][c].isMine) continue;
        let count = 0;
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = r + dr;
            const nc = c + dc;
            if (
              nr >= 0 && nr < rows &&
              nc >= 0 && nc < cols &&
              this.state.grid[nr][nc].isMine
            ) {
              count++;
            }
          }
        }
        this.state.grid[r][c].adjacentMines = count;
      }
    }
  }

  /**
   * BFS cascade reveal — avoids recursion stack overflow on large grids.
   */
  private cascadeReveal(startRow: number, startCol: number): void {
    const { rows, cols } = this.state.difficulty;
    const queue: Array<[number, number]> = [[startRow, startCol]];
    const visited = new Set<number>();
    visited.add(startRow * cols + startCol);

    while (queue.length > 0) {
      const [r, c] = queue.shift()!;

      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const nr = r + dr;
          const nc = c + dc;
          if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
          const key = nr * cols + nc;
          if (visited.has(key)) continue;
          visited.add(key);

          const neighbor = this.state.grid[nr][nc];
          if (neighbor.isRevealed || neighbor.isFlagged || neighbor.isMine) {
            continue;
          }

          this.mutateCell(nr, nc, { isRevealed: true });
          this.state.safeCellsRemaining -= 1;

          if (neighbor.adjacentMines === 0) {
            queue.push([nr, nc]);
          }
        }
      }
    }
  }

  // ─── Internals: Win / Lose ───────────────────────────────────────────────────

  private triggerLoss(triggerRow: number, triggerCol: number): void {
    this.stopTimer();
    this.state.phase = "LOSE";

    // Reveal all mines; mark incorrectly flagged cells
    const { rows, cols } = this.state.difficulty;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = this.state.grid[r][c];
        if (cell.isMine && !cell.isFlagged) {
          this.mutateCell(r, c, {
            isRevealed: true,
            // Tag the triggering mine for animation
            ...(r === triggerRow && c === triggerCol
              ? { adjacentMines: -1 } // sentinel: triggered mine
              : {}),
          });
        }
        if (cell.isFlagged && !cell.isMine) {
          this.mutateCell(r, c, { isRevealed: true }); // wrong flag
        }
      }
    }

    this.state.score = this.computeScore(true);
    this.onGameEnded?.(this.state.score, this.state.difficulty.leaderboardKey);
    this.emit();
  }

  private triggerWin(): void {
    this.stopTimer();
    this.state.phase = "WIN";

    // Auto-flag all remaining unflagged mines
    const { rows, cols } = this.state.difficulty;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = this.state.grid[r][c];
        if (cell.isMine && !cell.isFlagged) {
          this.mutateCell(r, c, { isFlagged: true });
          this.state.flagsPlaced++;
        }
      }
    }

    this.state.score = this.computeScore(true);
    this.onGameEnded?.(this.state.score, this.state.difficulty.leaderboardKey);
    this.emit();
  }

  /**
   * Timed levels (6B): the countdown expired — a loss with no detonated
   * cell. Phase flips before stopTimer so the timer's wall-clock capture
   * doesn't push timeElapsed past the limit.
   */
  private triggerTimeout(): void {
    this.state.phase = "LOSE";
    this.stopTimer();

    const { rows, cols } = this.state.difficulty;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = this.state.grid[r][c];
        if (cell.isMine && !cell.isFlagged) {
          this.mutateCell(r, c, { isRevealed: true });
        }
        if (cell.isFlagged && !cell.isMine) {
          this.mutateCell(r, c, { isRevealed: true }); // wrong flag
        }
      }
    }

    this.state.score = this.computeScore(true);
    this.onGameEnded?.(this.state.score, this.state.difficulty.leaderboardKey);
    this.emit();
  }

  // ─── Internals: Scoring ────────────────────────────────────────────────────────

  /**
   * Score calculation has two modes:
   *
   * LIVE (during play):
   *   Returns 0 — score is hidden entirely until game ends.
   *   Prevents leaking flag correctness information mid-game.
   *
   * FINAL (on WIN or LOSE):
   *   Score = Base + Win Bonus + Speed Bonus
   *
   *   Base        = (Correct Flags × 10) − (Wrong Flags × 5)
   *   Win Bonus   = +100 on full board clear
   *   Speed Bonus = tiered reward based on elapsed time vs difficulty thresholds
   *                 (fast = +100, slower tiers = +75 / +50 / +25 / +0)
   *
   * This means:
   *   - Accuracy (flags) always contributes positively
   *   - Speed adds a bonus on top — fast play is rewarded, slow play is not punished
   *   - A slow accurate player still scores well; a fast careless player does not
   */
  private computeScore(final: boolean): number {
    if (!final) return 0;

    const { rows, cols, speedTiers } = this.state.difficulty;
    let correctFlags   = 0;
    let incorrectFlags = 0;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = this.state.grid[r][c];
        if (!cell.isFlagged) continue;
        if (cell.isMine) correctFlags++;
        else incorrectFlags++;
      }
    }

    const won        = this.state.phase === "WIN";
    const base       = (correctFlags * 10) - (incorrectFlags * 5);
    const winBonus   = won ? this.state.difficulty.winBonus : 0;
    // Speed bonus always applies — fast play is rewarded even on a blow-up.
    // Only the win bonus is withheld on a loss.
    const speedBonus = this.computeSpeedBonus(this.state.timeElapsed, speedTiers);

    return Math.max(0, base + winBonus + speedBonus);
  }

  /**
   * Walk through speed tiers (ascending by maxSeconds).
   * Return the bonus for the first tier whose maxSeconds >= elapsed.
   * If elapsed exceeds all tiers, return 0.
   *
   * Example (Easy): tiers = [[30,100],[60,75],[120,50],[300,25]]
   *   elapsed 25s  → 25 ≤ 30  → +100
   *   elapsed 45s  → 45 ≤ 60  → +75
   *   elapsed 400s → exceeds all → +0
   */
  private computeSpeedBonus(
    elapsed: number,
    tiers: Array<[number, number]>
  ): number {
    for (const [maxSeconds, bonus] of tiers) {
      if (elapsed <= maxSeconds) return bonus;
    }
    return 0;
  }

  // ─── Internals: Timer ────────────────────────────────────────────────────────

  /**
   * 2.0-B2: Use Date.now() deltas instead of incrementing a counter.
   * setInterval(1000) drifts and pauses in backgrounded tabs (iOS throttles
   * background timers). Storing the wall-clock start time and computing
   * elapsed on each tick gives accurate times even after backgrounding.
   *
   * Bug fix: only emit when timeElapsed actually changed (whole-second boundary).
   * Without this, the 500ms poll fires emit() every tick, causing hud.update()
   * to rewrite the flag counter DOM on every tick even when flags haven't moved.
   */
  private startTimer(): void {
    this.stopTimer();
    this.timerStartTime = Date.now();
    let lastEmittedSecond = -1;

    this.timerHandle = setInterval(() => {
      if (this.state.phase !== "PLAY") {
        this.stopTimer();
        return;
      }
      const newElapsed = Math.floor((Date.now() - this.timerStartTime!) / 1000);
      if (newElapsed === lastEmittedSecond) return; // no full second has passed yet
      lastEmittedSecond = newElapsed;
      this.state.timeElapsed = newElapsed;

      // Timed levels (6B): clock ran out
      const limit = this.state.difficulty.timeLimitSecs;
      if (limit !== undefined && newElapsed >= limit) {
        this.state.timeElapsed = limit;
        this.triggerTimeout();
        return;
      }

      this.state.score = this.computeScore(false);
      this.emit();
    }, 500); // poll at 500ms so we never miss a second boundary by more than 500ms
  }

  private stopTimer(): void {
    if (this.timerHandle !== null) {
      clearInterval(this.timerHandle);
      this.timerHandle = null;
    }
    // Capture final elapsed time so computeScore uses the real value at game end
    if (this.timerStartTime !== null && this.state.phase === "PLAY") {
      this.state.timeElapsed = Math.floor((Date.now() - this.timerStartTime) / 1000);
    }
    this.timerStartTime = null;
  }

  // ─── Internals: Helpers ──────────────────────────────────────────────────────

  /** Immutably patch a single cell (keeps reference stable for unchanged cells). */
  private mutateCell(
    row: number,
    col: number,
    patch: Partial<Cell>
  ): void {
    this.state.grid[row][col] = { ...this.state.grid[row][col], ...patch };
  }

  private emit(): void {
    this.onStateChange?.(this.state);
  }
}